/**
 * API 服务与流式响应工具
 */
import axios from 'axios';
import { ConfigData, KnowledgeBase, KnowledgeBaseFile, ModelConfigItem, OutlineData, OutlineItem, OutlineMode, ProjectInfo, TrashListResponse } from '../types';

// 动态构建 API 地址：使用当前访问的 hostname + 8000 端口
// 这样无论是 localhost、127.0.0.1 还是 192.168.x.x 都能正确访问后端
const getApiBaseUrl = (): string => {
  // 如果环境变量明确指定了 API 地址，优先使用
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // 否则根据当前页面访问地址动态构建（同 hostname，端口 8000）
  const hostname = window.location.hostname;
  // macOS 上 localhost 可能解析为 IPv6 ::1，导致跨域预检异常
  // 统一使用 127.0.0.1 避免 localhost 的 DNS 解析歧义
  const apiHost = hostname === 'localhost' ? '127.0.0.1' : hostname;
  return `http://${apiHost}:8000`;
};
export const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000,
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // 全局 401 处理：token 过期或无效时，清除登录状态并跳转登录页
    if (error?.response?.status === 401) {
      // 避免登录接口本身的 401 触发无限重定向（登录失败返回 success:false，不是 401）
      const isLoginRequest = error.config?.url?.includes('/api/auth/login');
      if (!isLoginRequest) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        window.location.reload();
      }
    }
    return Promise.reject(error);
  }
);

// 请求拦截器：自动附加 Token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface FileUploadResponse {
  success: boolean;
  message: string;
  file_content?: string;
  old_outline?: string;
  /** 多标段文档切分结果；无多标段时为 undefined 或单元素「整篇」 */
  sections?: Array<{ index: number; title: string; text: string }>;
}

/** 方案扩写文件信息（从后端加载的持久化记录） */
export interface ExpandFileInfo {
  id: number;
  name: string;
  size: number;
  file_type: string;
  created_at: string;
}

/** 方案扩写文件列表响应 */
export interface ExpandFilesResponse {
  success: boolean;
  message: string;
  files: ExpandFileInfo[];
  file_content?: string;
  old_outline?: string;
}

export interface ImageUploadResponse {
  success: boolean;
  message: string;
  url?: string;
  filename?: string;
}

export interface AnalysisRequest {
  file_content: string;
  analysis_type: string;
  /** 标段序号（多标段文档选定时传入；-1 / 不传表示整篇） */
  section_index?: number | null;
  /** 标段标题/包号（多标段文档选定时传入，用于限定后端分析范围） */
  section_title?: string | null;
}

export interface OutlineRequest {
  overview: string;
  requirements: string;
  mode?: OutlineMode;
  uploaded_expand?: boolean;
  old_outline?: string;
  old_document?: string;
  descriptive_level?: string;
  project_id?: number;
  project_overview?: string;
  tech_scoring?: string;
  tech_requirements?: string;
  biz_scoring?: string;
  bid_requirements?: string;
  commitment?: string;
  bid_document_format?: string;
  disqualification?: string;
}

export interface ChapterContentRequest {
  chapter: OutlineItem;
  parent_chapters?: OutlineItem[];
  sibling_chapters?: OutlineItem[];
  project_overview: string;
  tech_scoring?: string;
  tech_requirements?: string;
  section?: string;
  biz_scoring?: string;
  bid_requirements?: string;
  commitment?: string;
  bid_document_format?: string;
  disqualification?: string;
  project_id?: number;
}

export interface WordExportRequest {
  project_name?: string;
  outline: OutlineItem[];
  include_prompts?: boolean;
  project_overview?: string;
  template_id?: string;
  generation_mode?: string;
  /** 封面标题（招标文件导出传「招标文件」，优先于默认标题） */
  cover_title?: string;
}

export interface ExportTemplate {
  id: string;
  name: string;
  description: string;
  numbering: string;
  preview: string;
}

export interface StreamEvent {
  type?: 'progress' | 'result';
  chunk?: string;
  outline?: OutlineData;
  error?: boolean;
  message?: string;
}

const formatErrorDetail = (detail: unknown): string | null => {
  if (!detail) {
    return null;
  }

  if (typeof detail === 'string') {
    return detail;
  }

  if (Array.isArray(detail)) {
    const lines = detail
      .map((item) => {
        if (typeof item === 'string') {
          return item;
        }

        if (item && typeof item === 'object') {
          const maybeItem = item as { loc?: Array<string | number>; msg?: string };
          const path = maybeItem.loc?.join(' -> ');
          return path ? `${path}: ${maybeItem.msg || '参数校验失败'}` : (maybeItem.msg || '参数校验失败');
        }

        return null;
      })
      .filter((line): line is string => Boolean(line));

    return lines.length > 0 ? lines.join('；') : null;
  }

  if (detail && typeof detail === 'object') {
    const maybeDetail = detail as { detail?: unknown; message?: string };
    return formatErrorDetail(maybeDetail.detail) || maybeDetail.message || JSON.stringify(detail);
  }

  return String(detail);
};

export const getErrorMessage = (error: unknown, fallback = '请求失败'): string => {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    const message = error.response?.data?.message;
    return formatErrorDetail(detail) || message || error.message || fallback;
  }

  if (error instanceof Error) {
    return error.message || fallback;
  }

  return fallback;
};

// ===================== 后台评审 API =====================

export interface ReviewSubmitResult {
  success: boolean;
  message: string;
  history_id: number;
}

export interface ReviewStatusResult {
  success: boolean;
  history_id: number;
  phase: string;
  message: string;
  total_bids: number;
  completed_bids: number;
  db_phase: string;
  elapsed_str?: string;
}

export interface ReviewResultData {
  success: boolean;
  history_id: number;
  bidding_file_name: string;
  bid_file_names: string[];
  bidding_analysis: any[];
  bid_scores: any[];
  score_matrix: any;
  comparison: any;
  disqualification_check: any;
  risk_report: string;
  risk_level: string;
  total_bids: number;
  elapsed_str: string;
  phase: string;
  created_at: string;
}

/** 提交标书评审（后台异步执行） */
export const submitReview = async (
  biddingFile: File,
  bidFiles: File[],
  token: string,
): Promise<ReviewSubmitResult> => {
  const formData = new FormData();
  formData.append('bidding_file', biddingFile);
  bidFiles.forEach((f) => formData.append('bid_files', f));

  const res = await axios.post(`${API_BASE_URL}/api/tools/review/submit`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${token}`,
    },
    timeout: 600000, // 10 分钟，支持大文件上传（招标文件 + 多份投标文件可能达数百 MB）
    maxContentLength: Infinity,
    maxBodyLength: Infinity,
  });
  return res.data;
};

/** 提交正式评审（扫描件 PDF，后台 OCR 识别） */
export const submitFormalReview = async (
  biddingFile: File,
  bidFiles: File[],
  token: string,
): Promise<ReviewSubmitResult> => {
  const formData = new FormData();
  formData.append('bidding_file', biddingFile);
  bidFiles.forEach((f) => formData.append('bid_files', f));

  const res = await axios.post(`${API_BASE_URL}/api/tools/review/formal-submit`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${token}`,
    },
    timeout: 600000, // 10 分钟
    maxContentLength: Infinity,
    maxBodyLength: Infinity,
  });
  return res.data;
};

/** 订阅评审进度 SSE 流 */
export const subscribeReviewProgress = async (
  historyId: number,
  token: string,
  onEvent: (event: any) => void,
  signal?: AbortSignal,
): Promise<void> => {
  const res = await fetch(
    `${API_BASE_URL}/api/tools/review/${historyId}/stream`,
    {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    },
  );

  await readSseStream(res, onEvent, '评审进度流中断', signal);
};

/** 轮询评审状态（轻量级 JSON，SSE 不可用时的兜底） */
export const pollReviewStatus = async (
  historyId: number,
  token: string,
): Promise<ReviewStatusResult> => {
  const res = await axios.get(
    `${API_BASE_URL}/api/tools/review/${historyId}/status`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return res.data;
};

/** 获取评审完整结果 */
export const fetchReviewResult = async (
  historyId: number,
  token: string,
): Promise<ReviewResultData> => {
  const res = await axios.get(
    `${API_BASE_URL}/api/tools/review/${historyId}/result`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return res.data;
};

// ===================== 工具函数 =====================

const ensureResponseOk = async (response: Response, fallback: string): Promise<Response> => {
  if (response.ok) {
    return response;
  }

  try {
    const data = await response.json();
    throw new Error(formatErrorDetail(data.detail) || data.message || fallback);
  } catch (error) {
    if (error instanceof Error && error.message !== 'Unexpected end of JSON input') {
      throw error;
    }
  }

  const text = await response.text();
  throw new Error(text || fallback);
};

export const readSseStream = async (
  response: Response,
  onEvent: (event: StreamEvent) => void,
  fallbackMessage: string,
  signal?: AbortSignal,
): Promise<void> => {
  await ensureResponseOk(response, fallbackMessage);

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('无法读取响应流');
  }

  // 监听取消信号
  if (signal) {
    signal.addEventListener('abort', () => {
      reader.cancel().catch(() => {});
    });
  }

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    // 检查是否已被取消
    if (signal?.aborted) {
      throw new DOMException('用户取消了生成操作', 'AbortError');
    }

    const { done, value } = await reader.read();
    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split('\n\n');
    buffer = events.pop() || '';

    for (const eventBlock of events) {
      const dataLine = eventBlock
        .split('\n')
        .find((line) => line.startsWith('data: '));

      if (!dataLine) {
        continue;
      }

      const data = dataLine.slice(6);
      if (data === '[DONE]') {
        return;
      }

      let event: StreamEvent | null = null;
      try {
        event = JSON.parse(data) as StreamEvent;
      } catch {
        // 忽略非法片段，等待后续完整数据
        continue;
      }

      onEvent(event);
    }
  }
};

export const collectSseText = async (
  response: Response,
  onText?: (fullText: string, chunk: string) => void,
  fallbackMessage = '流式请求失败',
  signal?: AbortSignal,
): Promise<string> => {
  let fullText = '';

  await readSseStream(
    response,
    (event) => {
      if (event.error) {
        throw new Error(event.message || fallbackMessage);
      }

      if (!event.chunk) {
        return;
      }

      fullText += event.chunk;
      onText?.(fullText, event.chunk);
    },
    fallbackMessage,
    signal,
  );

  return fullText;
};

const postJson = (path: string, data: unknown, signal?: AbortSignal) => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const token = localStorage.getItem('auth_token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
    signal,
  });
};

export const configApi = {
  saveConfig: (config: ConfigData) => api.post('/api/config/save', config),
  loadConfig: () => api.get<ConfigData>('/api/config/load'),
  getModels: (config: ConfigData) => api.post<{ models: string[]; success: boolean; message: string }>('/api/config/models', config),
};

export interface ExportTaskInfo {
  task_id: number;
  project_name: string;
  filename: string;
  total: number;
  completed: number;
  current: string;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  error_message: string | null;
  downloaded: boolean;
  user_id: number | null;
  file_size: number;
  created_at: string | null;
  completed_at: string | null;
  failed_at: string | null;
}

export interface ExportTaskListResponse {
  tasks: ExportTaskInfo[];
  total: number;
}

export const documentApi = {
  uploadFile: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<FileUploadResponse>('/api/document/upload', formData);
  },
  uploadFileWithProject: (file: File, projectId?: number) => {
    const formData = new FormData();
    formData.append('file', file);
    if (projectId) {
      formData.append('project_id', String(projectId));
    }
    return api.post<FileUploadResponse>('/api/document/upload', formData, {
      timeout: 600000, // 10 分钟，与投标文件等上传接口对齐；标书含大量嵌入图片时文本提取+图片并发上传耗时较长
    });
  },
  /** 上传图片，返回可访问的 URL */
  uploadImage: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<ImageUploadResponse>('/api/document/upload-image', formData);
  },
  /** 获取所有已上传的图片列表（用于编辑器图库） */
  listImages: () =>
    api.get<{ images: Array<{ url: string; filename: string; size: number; created_at: number }>; total: number }>(
      '/api/document/list-images',
    ),
  analyzeDocumentStream: (data: AnalysisRequest) => postJson('/api/document/analyze-stream', data),
  exportWord: async (data: WordExportRequest) => ensureResponseOk(await postJson('/api/document/export-word', data), '导出失败'),
  /** 后台模式：提交 Word 导出任务 */
  exportWordBatch: (data: WordExportRequest) =>
    api.post<{ success: boolean; message: string; task_id: number }>('/api/document/export-word-batch', data, { timeout: 30000 }),
  /** 查询 Word 导出任务进度 */
  getExportStatus: (taskId: number) =>
    api.get<{ task_id: number; total: number; completed: number; current: string; status: string; filename: string; error_message: string | null }>(`/api/document/export-status/${taskId}`),
  /** 获取所有导出任务列表 */
  listExportTasks: () =>
    api.get<ExportTaskListResponse>('/api/document/export-tasks'),
  /** 删除导出任务 */
  deleteExportTask: (taskId: number) =>
    api.delete(`/api/document/export-tasks/${taskId}`),
  /** 下载已完成的 Word 导出文件（返回 blob URL） */
  getExportDownloadUrl: (taskId: number) => `${API_BASE_URL}/api/document/export-download/${taskId}`,
  exportMarkdown: (data: WordExportRequest) => postJson('/api/document/export-markdown', data),
  /** 获取 Word 导出目录模板列表 */
  getExportTemplates: () =>
    api.get<{ templates: ExportTemplate[] }>('/api/document/export-templates'),
};

export const outlineApi = {
  generateOutline: (data: OutlineRequest) => api.post<OutlineData>('/api/outline/generate', data),
  generateOutlineStream: (data: OutlineRequest) => postJson('/api/outline/generate-stream', data),
  /** 生成商务部分目录 */
  generateBizOutline: (data: {
    overview?: string;
    biz_scoring?: string;
    bid_requirements?: string;
    disqualification?: string;
    commitment?: string;
    bid_document_format?: string;
    tech_requirements?: string;
    descriptive_level?: string;
  }) => api.post<OutlineData>('/api/outline/generate-biz', data),
  /** 流式生成商务部分目录 */
  generateBizOutlineStream: (data: {
    overview?: string;
    biz_scoring?: string;
    bid_requirements?: string;
    disqualification?: string;
    commitment?: string;
    bid_document_format?: string;
    tech_requirements?: string;
    mode?: OutlineMode;
    descriptive_level?: string;
  }) => postJson('/api/outline/generate-biz-stream', data),
};

export interface BatchGenerateRequest {
  project_id: number;
  chapters: Record<string, any>[];
  project_overview?: string;
  tech_scoring?: string;
  tech_requirements?: string;
  section?: string;
  biz_scoring?: string;
  bid_requirements?: string;
  commitment?: string;
  bid_document_format?: string;
  disqualification?: string;
  concurrency?: number;
}

export interface BatchGenerateResponse {
  success: boolean;
  message: string;
  task_id: number;
}

export interface GenerateStatusResponse {
  task_id: number;
  project_id: number;
  total: number;
  completed: number;
  failed: number;
  current: string;
  failed_titles: string[];
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  results: Record<string, string>;
}

export interface ProjectBatchTaskResponse {
  has_task: boolean;
  task_id?: number;
  total?: number;
  completed?: number;
  failed?: number;
  current?: string;
  failed_titles?: string[];
  status?: string;
}

export const contentApi = {
  generateChapterContent: (data: ChapterContentRequest) => api.post<{ success: boolean; content: string }>('/api/content/generate-chapter', data),
  generateChapterContentStream: (data: ChapterContentRequest, signal?: AbortSignal) => postJson('/api/content/generate-chapter-stream', data, signal),
  /** 后台模式：提交批量生成任务 */
  generateAllBatch: (data: BatchGenerateRequest) =>
    api.post<BatchGenerateResponse>('/api/content/generate-all-batch', data, { timeout: 30000 }),
  /** 取消后台生成任务 */
  cancelBatch: (taskId: number) =>
    api.post<{ success: boolean; message: string }>(`/api/content/cancel-batch/${taskId}`),
  /** 查询后台生成任务进度 */
  getGenerateStatus: (taskId: number) =>
    api.get<GenerateStatusResponse>(`/api/content/generate-status/${taskId}`),
  /** 查询项目是否有运行中的后台生成任务（多设备一致性） */
  getProjectBatchTask: (projectId: number) =>
    api.get<ProjectBatchTaskResponse>(`/api/content/project/${projectId}/batch-task`),
  /** 修复 mermaid 图表语法错误 */
  fixMermaid: (data: { content: string; errors: string[] }) =>
    api.post<{ success: boolean; content: string }>('/api/content/fix-mermaid', data),
  /** 根据上下文或提词生成 Mermaid 图表 */
  generateMermaid: (data: { context: string }) =>
    api.post<{ success: boolean; content: string }>('/api/content/generate-mermaid', data),
  /** 前台生成完成后刷新项目状态（草稿 → 编辑中 / 已完成） */
  refreshProjectStatus: (projectId: number) =>
    api.post<{ ok: boolean }>(`/api/content/refresh-project-status/${projectId}`),
};

export const expandApi = {
  /** 获取已保存的方案扩写文件列表及合并内容 */
  getExpandFiles: (projectId?: number) =>
    api.get<ExpandFilesResponse>('/api/expand/files', {
      params: projectId != null ? { project_id: projectId } : undefined,
    }),

  /** 上传方案扩写参考文档（支持多文件，返回全部文件列表及合并内容） */
  uploadExpandFiles: (files: File[], projectId?: number) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return api.post<ExpandFilesResponse>('/api/expand/upload', formData, {
      timeout: 300000 * Math.max(files.length, 1),
      params: projectId != null ? { project_id: projectId } : undefined,
    });
  },

  /** 删除单个方案扩写文件（返回更新后的文件列表及合并内容） */
  deleteExpandFile: (fileId: number, projectId?: number) =>
    api.delete<ExpandFilesResponse>(`/api/expand/files/${fileId}`, {
      params: projectId != null ? { project_id: projectId } : undefined,
    }),
};

export const uploadOutlineApi = {
  /** 上传目录结构文件（JSON/文本） */
  uploadOutlineFile: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<FileUploadResponse>('/api/upload-outline/from-file', formData, {
      timeout: 60000,
    });
  },
  /** 上传描述文件生成目录 */
  generateFromDescriptionFile: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<FileUploadResponse>('/api/upload-outline/from-description-file', formData, {
      timeout: 300000,
    });
  },
  /** 根据文档结构描述生成目录 */
  generateFromDescription: (overview: string, requirements: string) => {
    return api.post<FileUploadResponse>('/api/upload-outline/from-description', {
      overview,
      requirements,
    }, {
      timeout: 300000,
    });
  },
};

// ---------- 项目管理 API ----------

export interface ProjectCreateRequest {
  name: string;
  description?: string;
  knowledge_base_ids?: number[];
  outline_descriptive_level?: string;
  company_profile_ids?: number[];
  bid_total_price?: string | null;
  bid_duration?: string | null;
  project_manager?: string | null;
  contact_person?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  bid_agent?: string | null;
  other_info?: string | null;
}

export interface ProjectUpdateRequest {
  name?: string;
  description?: string;
  current_step?: number;
  generation_mode?: string;
  status?: string;
  knowledge_base_ids?: number[];
  model_config_id?: number | null;
  outline_descriptive_level?: string;
  company_profile_ids?: number[];
  bid_total_price?: string | null;
  bid_duration?: string | null;
  project_manager?: string | null;
  contact_person?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  bid_agent?: string | null;
  other_info?: string | null;
}

export interface ProjectListResponse {
  projects: ProjectInfo[];
  total: number;
}

export interface ProjectFullData {
  project_id: number;
  analysis_results: Array<{
    id: number;
    analysis_type: string;
    content: string;
  }>;
  outline_nodes: Array<{
    id: number;
    node_id: string;
    stable_id?: string | null;
    parent_node_id: string | null;
    title: string;
    description: string | null;
    word_count_range?: string | null;
    source_requirement_id: string | null;
    source_requirement_title: string | null;
    sort_order: number;
    section?: string;
  }>;
  chapter_contents: Array<{
    id: number;
    node_id: string;
    stable_id?: string | null;
    content: string | null;
    section?: string;
  }>;
  upload_files: Array<{
    id: number;
    original_name: string;
    file_type: string;
    extracted_text: string | null;
  }>;
  outline_source: string | null;
}

/** 按需获取的单个章节正文响应 */
export interface ChapterContentResponse {
  exists: boolean;
  content: string | null;
  version: number | null;
  updated_by_user_id: number | null;
  updated_at: string | null;
}

export const projectApi = {
  create: (data: ProjectCreateRequest) =>
    api.post<ProjectInfo>('/api/project/create', data),

  list: (source?: string) => api.get<ProjectListResponse>('/api/project/list', {
    params: source ? { source } : undefined,
  }),

  get: (projectId: number) =>
    api.get<ProjectInfo>(`/api/project/${projectId}`),

  update: (projectId: number, data: ProjectUpdateRequest) =>
    api.put<ProjectInfo>(`/api/project/${projectId}`, data),

  delete: (projectId: number) =>
    api.delete(`/api/project/${projectId}`),

  listTrash: () =>
    api.get<TrashListResponse>('/api/project/trash'),

  restore: (projectId: number) =>
    api.post(`/api/project/${projectId}/restore`),

  permanentDelete: (projectId: number) =>
    api.delete(`/api/project/${projectId}/permanent`),

  getFullData: (projectId: number) =>
    api.get<ProjectFullData>(`/api/project/${projectId}/data`),

  saveAnalysis: (projectId: number, analysisType: string, content: string) =>
    api.post(`/api/project/${projectId}/save-analysis`, { analysis_type: analysisType, content }),

  saveOutline: (projectId: number, nodes: Array<{
    id: string;
    stable_id?: string | null;
    parent_node_id?: string | null;
    title: string;
    description?: string;
    word_count_range?: string | null;
    source_requirement_id?: string | null;
    source_requirement_title?: string | null;
    section?: string;
  }>, outlineSource?: string) => api.post(`/api/project/${projectId}/save-outline`, {
    nodes,
    outline_source: outlineSource || null,
  }),

  saveChapterContent: (projectId: number, nodeId: string, content: string, section: string = 'tech', stableId?: string | null, version?: number | null) =>
    api.post(`/api/project/${projectId}/save-chapter-content`, {
      node_id: nodeId,
      content,
      section,
      stable_id: stableId || null,
      version: version ?? null,
    }),

  /** 按需获取单个章节正文（避免一次性加载整份标书全部正文），返回 content/version/updated_by_user_id/updated_at */
  getChapterContent: (projectId: number, params: { stable_id?: string | null; node_id?: string | null; section?: string }) =>
    api.get<ChapterContentResponse>(`/api/project/${projectId}/chapter-content`, { params }),

  /** 清空项目指定 section 的章节内容 */
  clearChapterContents: (projectId: number, section: string = 'tech') =>
    api.post<{ success: boolean; message: string }>(
      `/api/project/${projectId}/clear-chapter-contents?section=${section}`,
      {},
      { timeout: 30000 },
    ),

  /** AI 生成节点描述 */
  generateNodeDescription: (
    projectId: number,
    title: string,
    parentTitles?: string[],
  ) =>
    api.post<{ success: boolean; description: string }>(
      `/api/project/${projectId}/generate-node-description`,
      { title, parent_titles: parentTitles },
      { timeout: 30000 },
    ),

  // ---------- 项目附件管理 ----------

  /** 上传项目附件 */
  uploadAttachment: (projectId: number, file: File, description?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams();
    if (description) params.append('description', description);
    return api.post<import('../types').ProjectAttachment>(
      `/api/project/${projectId}/attachments?${params.toString()}`,
      formData,
      { timeout: 120000 },
    );
  },

  /** 获取项目附件列表 */
  listAttachments: (projectId: number) =>
    api.get<{ attachments: import('../types').ProjectAttachment[] }>(
      `/api/project/${projectId}/attachments`,
    ),

  /** 获取项目附件详情（含提取文本） */
  getAttachmentDetail: (projectId: number, attachmentId: number) =>
    api.get<import('../types').ProjectAttachmentDetail>(
      `/api/project/${projectId}/attachments/${attachmentId}`,
    ),

  /** 更新项目附件描述及提取文本 */
  updateAttachment: (projectId: number, attachmentId: number, params: { description?: string; extracted_text?: string }) =>
    api.put<import('../types').ProjectAttachment>(
      `/api/project/${projectId}/attachments/${attachmentId}`,
      params,
    ),

  /** 删除项目附件 */
  deleteAttachment: (projectId: number, attachmentId: number) =>
    api.delete<{ success: boolean; message: string }>(
      `/api/project/${projectId}/attachments/${attachmentId}`,
    ),
};

// ---------- 版本管理 API ----------

export interface VersionSummary {
  id: number;
  version_name: string;
  version_description: string | null;
  total_tokens: number;
  generated_chapters: number;
  created_at: string | null;
}

export interface VersionDetail {
  id: number;
  version_name: string;
  version_description: string | null;
  total_tokens: number;
  generated_chapters: number;
  created_at: string | null;
  outline_nodes: Array<{
    node_id: string;
    parent_node_id: string | null;
    title: string;
    description: string;
    word_count_range?: string | null;
    source_requirement_id?: string | null;
    source_requirement_title?: string | null;
  }>;
  chapter_contents: Record<string, string>;  // {node_id: content}
}

export const versionApi = {
  /** 保存当前状态为版本快照 */
  saveVersion: (
    projectId: number,
    data: { version_name?: string; version_description?: string; total_tokens?: number; generated_chapters?: number; section?: string }
  ) => api.post(`/api/project/${projectId}/save-version`, data),

  /** 获取版本列表（可选按 section 过滤） */
  listVersions: (projectId: number, section?: string) =>
    api.get<{ versions: VersionSummary[]; total: number }>(`/api/project/${projectId}/versions`, {
      params: section ? { section } : undefined,
    }),

  /** 获取版本详情 */
  getVersionDetail: (projectId: number, versionId: number) =>
    api.get<VersionDetail>(`/api/project/${projectId}/version/${versionId}`),

  /** 恢复版本 */
  restoreVersion: (projectId: number, versionId: number) =>
    api.post(`/api/project/${projectId}/version/${versionId}/restore`),

  /** 导出版本 Markdown */
  exportVersionMarkdown: (projectId: number, versionId: number) =>
    `${API_BASE_URL}/api/project/${projectId}/version/${versionId}/export`,

  /** 导出版本 Word（后台任务模式，返回 task_id） */
  exportVersionWord: (projectId: number, versionId: number, templateId: string) =>
    api.post<{ success: boolean; message: string; task_id: number }>(
      `/api/project/${projectId}/version/${versionId}/export-word`,
      { template_id: templateId },
      { timeout: 30000 },
    ),

  /** 删除版本 */
  deleteVersion: (projectId: number, versionId: number) =>
    api.delete(`/api/project/${projectId}/version/${versionId}`),

  /** 更新版本中单个章节的内容（用于版本对比中编辑保存） */
  updateVersionChapterContent: (
    projectId: number,
    versionId: number,
    nodeId: string,
    content: string
  ) =>
    api.put(`/api/project/${projectId}/version/${versionId}/chapter/${nodeId}`, { content }),
};

// ---------- 认证 API ----------

export const authApi = {
  /** 用户名+密码登录 */
  login: (username: string, password: string) =>
    api.post('/api/auth/login', { username, password }),

  /** 微信登录 */
  wechatLogin: (code: string) =>
    api.post('/api/auth/wechat-login', { code }),

  /** Casdoor OAuth 登录 */
  casdoorLogin: (code: string, ref?: string | null) =>
    api.post('/api/auth/casdoor/login', { code, ref }),

  /** 获取 Casdoor OAuth 配置 */
  getCasdoorConfig: () =>
    api.get<{ enabled: boolean; auth_url: string; redirect_uri: string }>('/api/auth/casdoor/config'),

  /** 获取当前用户信息 */
  getMe: () => api.get('/api/auth/me'),

  /** 登出 */
  logout: () => api.post('/api/auth/logout'),

  /** 前端心跳：维护在线状态 */
  heartbeat: () => api.post<{ success: boolean }>('/api/auth/heartbeat'),

  /** 获取用户列表（管理员，含在线状态/时长、项目数、充值金额、最近登录与 online_count），支持筛选与分页 */
  listUsers: (params?: {
    username?: string;
    min_recharge?: number;
    min_projects?: number;
    page?: number;
    page_size?: number;
  }) => {
    const p: Record<string, unknown> = {};
    if (params?.username) p.username = params.username;
    if (params?.min_recharge != null) p.min_recharge = params.min_recharge;
    if (params?.min_projects != null) p.min_projects = params.min_projects;
    if (params?.page != null) p.page = params.page;
    if (params?.page_size != null) p.page_size = params.page_size;
    return api.get<{
      users: Array<import('../types').UserInfo & { is_online: boolean; online_seconds: number; last_login_at?: string | null; total_online_seconds?: number; project_count?: number; recharge_amount?: number }>;
      total: number; online_count: number; page: number; page_size: number; total_pages: number;
    }>('/api/auth/users', { params: p });
  },

  /** 管理员：单用户详情（查看按钮用） */
  getUser: (userId: number) =>
    api.get<{
      id: number; username: string; email: string | null; display_name: string | null; role: string;
      is_active: boolean; points: number; total_points_used: number; created_at: string | null;
      last_login_at: string | null; total_online_seconds: number; current_online_seconds: number;
      project_count: number; enterprise_count: number; kb_count: number; recharge_total_amount: number;
    }>(`/api/auth/users/${userId}`),

  /** 管理员：所有用户积分列表（总/已用/剩余），支持关键字搜索与分页 */
  listUserPoints: (params?: { keyword?: string; page?: number; page_size?: number }) => {
    const p: Record<string, unknown> = {};
    if (params?.keyword) p.keyword = params.keyword;
    if (params?.page != null) p.page = params.page;
    if (params?.page_size != null) p.page_size = params.page_size;
    return api.get<{
      users: Array<{ id: number; username: string; display_name: string | null; role: string; points: number; total_points_used: number; total_points: number }>;
      total: number; page: number; page_size: number; total_pages: number;
    }>('/api/auth/admin/points/users', { params: p });
  },

  /** 管理员：给指定用户充值 */
  rechargeUserPoints: (data: { user_id: number; points: number; note?: string }) =>
    api.post<{ success: boolean; message: string; points_added: number; points_balance: number }>('/api/auth/admin/points/recharge', data),

  /** 创建用户（管理员） */
  createUser: (data: { username: string; password: string; display_name?: string; role: string }) =>
    api.post('/api/auth/users', data),

  /** 更新用户（管理员） */
  updateUser: (userId: number, data: { display_name?: string; password?: string; role?: string; is_active?: boolean }) =>
    api.put(`/api/auth/users/${userId}`, data),

  /** 删除用户（管理员） */
  deleteUser: (userId: number) =>
    api.delete(`/api/auth/users/${userId}`),

  /** 修改密码 */
  changePassword: (oldPassword: string, newPassword: string) =>
    api.post('/api/auth/change-password', { old_password: oldPassword, new_password: newPassword }),

  /** 更新个人信息（昵称、邮箱） */
  updateProfile: (data: { display_name?: string; email?: string }) =>
    api.put('/api/auth/profile', data),

  /** 获取用户统计信息 */
  getStats: () => api.get<{ project_count: number; tokens_used: number; tokens_balance: number; user_type: string }>('/api/auth/stats'),

  /** 获取各项目 Token 消耗明细 */
  getProjectTokens: () => api.get<{ projects: Array<{ project_id: number; project_name: string; tokens_used: number }>; total: number }>('/api/auth/stats/projects'),

  /** 获取积分消费流水明细（支持时间区间和类型筛选） */
  getPointsTransactions: (params?: {
    start_date?: string;
    end_date?: string;
    type_filter?: string;
    page?: number;
    page_size?: number;
  }) => api.get<import('../types').PointsTransactionListResponse>('/api/auth/points/transactions', { params }),

  /** 获取项目消费统计 */
  getProjectConsumptionStats: (params?: {
    start_date?: string;
    end_date?: string;
  }) => api.get<import('../types').ProjectConsumptionStatsResponse>('/api/auth/points/project-stats', { params }),

  /** 充值 */
  recharge: (plan: string) =>
    api.post<{ success: boolean; message: string; tokens_added: number; tokens_balance: number }>('/api/auth/recharge', { plan }),

  /** 获取当前用户的推荐码和累计推荐奖励 */
  getReferralCode: () =>
    api.get<import('../types').ReferralCodeInfo>('/api/auth/referral-code'),

  /** 获取推荐统计：被推荐用户列表和累计奖励 */
  getReferralStats: () =>
    api.get<import('../types').ReferralStats>('/api/auth/referral-stats'),

  /** 验证推荐码是否有效（公开接口） */
  checkReferralCode: (code: string) =>
    api.get<import('../types').ReferralCheckResult>('/api/auth/referral/check', { params: { code } }),
};

// ---------- 支付 API（充值积分） ----------

export const payApi = {
  /** 创建充值订单（选套餐 + alipay/wechat），返回支付信息(pay_url/pay_page_url/out_trade_no) */
  order: (plan: string, payment_method: string) =>
    api.post<{ order_no: string; amount: number; points: number; payment_method: string; out_trade_no: string | null; pay_url: string | null; qrcode_img: string | null; pay_page_url: string | null }>(
      '/api/pay/order', { plan, payment_method }),
  /** 查询订单状态（前端轮询到账） */
  status: (orderNo: string) =>
    api.get<{ order_no: string; status: 'pending' | 'paid' | 'cancelled' | 'failed'; amount: number; points: number; pay_url: string | null; qrcode_img: string | null; paid_at: string | null }>(
      `/api/pay/order/${orderNo}`),
  /** 取消待支付订单 */
  cancel: (orderNo: string) =>
    api.post<{ success: boolean; message: string }>(`/api/pay/cancel/${orderNo}`),
  /** 上架中的充值套餐（充值页用，来自 recharge_plan 表） */
  plans: () =>
    api.get<{ plans: Array<{ id: number; key: string; name: string; price: number; points: number; is_active: boolean; sort: number }> }>('/api/pay/plans'),
};

// ---------- 支付管理 API（管理员：充值管理） ----------

export const payAdminApi = {
  /** 收入统计：累计收入 + 按月 */
  stats: () =>
    api.get<{ total_revenue: number; paid_count: number; monthly: Array<{ month: string; amount: number }> }>('/api/pay/admin/stats'),
  /** 充值记录（分页） */
  records: (page = 1, page_size = 20) =>
    api.get<{ total: number; records: Array<{ order_no: string; user_id: number; username: string; amount: number; points: number; payment_method: string; user_points: number; paid_at: string | null }> }>('/api/pay/admin/records', { params: { page, page_size } }),
  /** 某用户累计充值 */
  userTotal: (userId: number) =>
    api.get<{ total_amount: number; total_points: number; count: number }>(`/api/pay/admin/user-total/${userId}`),
  /** 全部套餐（管理） */
  plans: () =>
    api.get<{ plans: Array<{ id: number; key: string; name: string; price: number; points: number; is_active: boolean; sort: number }> }>('/api/pay/admin/plans'),
  createPlan: (data: { key: string; name: string; price: number; points: number; is_active: boolean; sort: number }) =>
    api.post<{ ok: boolean; id: number }>('/api/pay/admin/plans', data),
  updatePlan: (id: number, data: { key: string; name: string; price: number; points: number; is_active: boolean; sort: number }) =>
    api.put<{ ok: boolean }>(`/api/pay/admin/plans/${id}`, data),
  deletePlan: (id: number) =>
    api.delete<{ ok: boolean }>(`/api/pay/admin/plans/${id}`),
};

// ---------- 系统配置 API（管理员） ----------

export interface FetchModelsResponse {
  success: boolean;
  message: string;
  models: string[];
}

// ---------- 系统授权 API ----------

export const systemLicenseApi = {
  /** 机器码 + 授权状态 + 详情 */
  getStatus: () =>
    api.get<{ status: string; machine_id: string; message: string; license: any }>('/api/system/license'),
  /** 导入并激活授权 */
  import: (content: string) =>
    api.post<{ ok: boolean; status?: string; message: string; license?: any }>('/api/system/license/import', { content }),
  /** 生成授权申请文件内容 */
  request: (data: { company: string; contact: string }) =>
    api.post<{ ok: boolean; machine_id: string; request: any }>('/api/system/license/request', data),
  /** 移除授权 */
  remove: () => api.delete<{ ok: boolean; message: string }>('/api/system/license'),
};

export const systemConfigApi = {
  /** 获取系统级大模型配置 */
  getConfig: () => api.get('/api/system/config'),

  /** 设置系统级配置（管理员），包含大模型配置和生成设置 */
  setConfig: (data: { base_url?: string; api_key?: string; model?: string; concurrency?: number; mermaid_default_view?: string; mermaid_export_format?: string; company_att_text_limit?: number; project_att_text_limit?: number; kb_max_chunks?: number; kb_max_total_chars?: number; kb_retrieval_mode?: string; llm_temperature?: number; prompt_context_tech?: string; prompt_context_biz?: string; casdoor_enabled?: boolean; new_user_points?: number; poster_slogans?: string; referral_reward_ratio?: number }) =>
    api.post('/api/system/config', data),

  /** 根据 base_url + api_key 获取可用模型列表（管理员）；include_all 可绕过关键字过滤返回全部模型 */
  fetchModels: (data: { base_url?: string; api_key?: string; model_hint?: string; include_all?: boolean }) =>
    api.post<FetchModelsResponse>('/api/system/fetch-models', data),

  /** 获取角色权限配置（管理员） */
  getRolePermissions: () =>
    api.get<{ success: boolean; permissions: Record<string, string[]>; all_permissions: Array<{ key: string; label: string; description: string }> }>('/api/system/role-permissions'),

  /** 设置角色权限（管理员） */
  setRolePermissions: (role: string, permissions: string[]) =>
    api.post('/api/system/role-permissions', { role, permissions }),
};

// ---------- 大模型配置管理 API ----------

export interface ModelConfigListResponse {
  configs: ModelConfigItem[];
  total: number;
}

export const modelConfigApi = {
  // ---------- 管理员 ----------
  /** 获取所有模型配置列表（管理员） */
  adminList: () => api.get<ModelConfigListResponse>('/api/model-config/admin/list'),

  /** 创建模型配置（管理员） */
  adminCreate: (data: { name: string; base_url: string; api_key: string; model: string; is_default?: boolean; is_shared?: boolean; is_active?: boolean; sort_order?: number; temperature?: number; max_context_tokens?: number }) =>
    api.post<ModelConfigItem>('/api/model-config/admin/create', data),

  /** 更新模型配置（管理员） */
  adminUpdate: (configId: number, data: { name?: string; base_url?: string; api_key?: string; model?: string; is_default?: boolean; is_shared?: boolean; is_active?: boolean; sort_order?: number; temperature?: number; max_context_tokens?: number }) =>
    api.put<ModelConfigItem>(`/api/model-config/admin/${configId}`, data),

  /** 删除模型配置（管理员） */
  adminDelete: (configId: number) =>
    api.delete(`/api/model-config/admin/${configId}`),

  // ---------- 普通用户 ----------
  /** 获取可用模型配置列表 */
  getAvailable: () => api.get<ModelConfigListResponse>('/api/model-config/available'),

  /** 选择使用的模型配置 */
  select: (modelConfigId: number | null) =>
    api.post<{ success: boolean; message: string; model_config_id: number | null }>('/api/model-config/select', { model_config_id: modelConfigId }),

  /** 获取当前选择的模型 */
  getCurrent: () => api.get<{ model_config_id: number | null; config: ModelConfigItem | null }>('/api/model-config/current'),
};

// ---------- 知识库管理 API ----------

export interface KnowledgeBaseListResponse {
  knowledge_bases: KnowledgeBase[];
  total: number;
}

export interface KnowledgeBaseFileListResponse {
  files: KnowledgeBaseFile[];
  total: number;
}

export const knowledgeApi = {
  /** 创建知识库 */
  create: (data: { name: string; description?: string }) =>
    api.post<KnowledgeBase>('/api/knowledge/create', data),

  /** 获取知识库列表 */
  list: (source?: string) => api.get<KnowledgeBaseListResponse>('/api/knowledge/list', {
    params: source ? { source } : undefined,
  }),

  /** 修改知识库 */
  update: (kbId: number, data: { name?: string; description?: string }) =>
    api.put<KnowledgeBase>(`/api/knowledge/${kbId}`, data),

  /** 删除知识库 */
  delete: (kbId: number) =>
    api.delete<{ success: boolean; message: string }>(`/api/knowledge/${kbId}`),

  /** 上传单文件到知识库 */
  uploadFile: (kbId: number, file: File) => {
    const formData = new FormData();
    formData.append('files', file);
    return api.post(`/api/knowledge/${kbId}/upload`, formData, {
      timeout: 120000,
    });
  },

  /** 批量上传文件到知识库 */
  uploadFiles: (kbId: number, files: File[]) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return api.post(`/api/knowledge/${kbId}/upload`, formData, {
      timeout: 600000,
    });
  },

  /** 删除知识库中的文件 */
  deleteFile: (kbId: number, fileId: number) =>
    api.delete<{ success: boolean; message: string }>(`/api/knowledge/${kbId}/file/${fileId}`),

  /** 列出知识库内文件 */
  listFiles: (kbId: number) =>
    api.get<KnowledgeBaseFileListResponse>(`/api/knowledge/${kbId}/files`),

  /** 查看文件分块 */
  getFileChunks: (kbId: number, fileId: number) =>
    api.get<import('../types').FileChunksResponse>(`/api/knowledge/${kbId}/file/${fileId}/chunks`),

  /** 获取文件详情（含提取文本） */
  getFile: (kbId: number, fileId: number) =>
    api.get<KnowledgeBaseFile & { extracted_text: string }>(`/api/knowledge/${kbId}/file/${fileId}`),

  /** 更新文件提取文本 */
  updateFile: (kbId: number, fileId: number, extracted_text: string) =>
    api.put<{ success: boolean; message: string }>(`/api/knowledge/${kbId}/file/${fileId}`, { extracted_text }),

  /** 上传图片到知识库（编辑器专用） */
  uploadImage: (kbId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<{ success: boolean; url: string; filename: string }>(
      `/api/knowledge/${kbId}/upload-image`,
      formData,
      { timeout: 30000 },
    );
  },

  /** 获取知识库中所有图片 */
  listImages: (kbId: number) =>
    api.get<{ images: Array<{ url: string; page?: number | null; index?: number; description?: string; file_name?: string; file_id?: number }>; total: number }>(
      `/api/knowledge/${kbId}/images`,
    ),
};

// ---------- 企业信息库 API ----------

export interface CompanyProfileListResponse {
  companies: import('../types').CompanyProfile[];
  total: number;
}

export interface CompanyFinancialInfoListResponse {
  financial_infos: import('../types').CompanyFinancialInfo[];
}

export interface CompanyAttachmentListResponse {
  attachments: import('../types').CompanyAttachment[];
}

export const companyApi = {
  /** 创建企业信息 */
  create: (data: {
    name: string;
    address?: string | null;
    legal_representative?: string | null;
    legal_representative_id_number?: string | null;
    unified_social_credit_code?: string | null;
    bank_name?: string | null;
    bank_account?: string | null;
    company_phone?: string | null;
    fax?: string | null;
    postal_code?: string | null;
    notes?: string | null;
  }) => api.post<import('../types').CompanyProfile>('/api/company/create', data),

  /** 获取企业信息列表 */
  list: (source?: string) => api.get<CompanyProfileListResponse>('/api/company/list', {
    params: source ? { source } : undefined,
  }),

  /** 获取单个企业信息 */
  get: (companyId: number) =>
    api.get<import('../types').CompanyProfile>(`/api/company/${companyId}`),

  /** 更新企业信息 */
  update: (companyId: number, data: {
    name?: string;
    address?: string | null;
    legal_representative?: string | null;
    legal_representative_id_number?: string | null;
    unified_social_credit_code?: string | null;
    bank_name?: string | null;
    bank_account?: string | null;
    company_phone?: string | null;
    fax?: string | null;
    postal_code?: string | null;
    notes?: string | null;
    is_default?: boolean;
  }) => api.put<import('../types').CompanyProfile>(`/api/company/${companyId}`, data),

  /** 删除企业信息 */
  delete: (companyId: number) =>
    api.delete<{ success: boolean; message: string }>(`/api/company/${companyId}`),

  // ---------- 财务信息 ----------

  /** 创建财务信息 */
  createFinancialInfo: (companyId: number, data: {
    year: string;
    total_assets?: string | null;
    net_assets?: string | null;
    revenue?: string | null;
    net_profit?: string | null;
    r_and_d_expenses?: string | null;
    notes?: string | null;
  }) => api.post<import('../types').CompanyFinancialInfo>(`/api/company/${companyId}/financial-info`, data),

  /** 获取财务信息列表 */
  listFinancialInfos: (companyId: number) =>
    api.get<CompanyFinancialInfoListResponse>(`/api/company/${companyId}/financial-info`),

  /** 更新财务信息 */
  updateFinancialInfo: (companyId: number, fiId: number, data: {
    year?: string;
    total_assets?: string | null;
    net_assets?: string | null;
    revenue?: string | null;
    net_profit?: string | null;
    r_and_d_expenses?: string | null;
    notes?: string | null;
  }) => api.put<import('../types').CompanyFinancialInfo>(`/api/company/${companyId}/financial-info/${fiId}`, data),

  /** 删除财务信息 */
  deleteFinancialInfo: (companyId: number, fiId: number) =>
    api.delete<{ success: boolean; message: string }>(`/api/company/${companyId}/financial-info/${fiId}`),

  // ---------- 附件管理 ----------

  /** 上传企业附件 */
  uploadAttachment: (companyId: number, file: File, category: string, description?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams({ category });
    if (description) params.append('description', description);
    return api.post<import('../types').CompanyAttachment>(
      `/api/company/${companyId}/attachments?${params.toString()}`,
      formData,
      { timeout: 120000 },
    );
  },

  /** 获取附件列表（可按类别筛选） */
  listAttachments: (companyId: number, category?: string) =>
    api.get<CompanyAttachmentListResponse>(`/api/company/${companyId}/attachments`, {
      params: category ? { category } : undefined,
    }),

  /** 获取附件详情（含提取文本） */
  getAttachmentDetail: (companyId: number, attachmentId: number) =>
    api.get<import('../types').CompanyAttachmentDetail>(`/api/company/${companyId}/attachments/${attachmentId}`),

  /** 更新附件描述及提取文本 */
  updateAttachment: (companyId: number, attachmentId: number, params: { description?: string; extracted_text?: string }) =>
    api.put<import('../types').CompanyAttachment>(`/api/company/${companyId}/attachments/${attachmentId}`, params),

  /** 删除附件 */
  deleteAttachment: (companyId: number, attachmentId: number) =>
    api.delete<{ success: boolean; message: string }>(`/api/company/${companyId}/attachments/${attachmentId}`),
};

// ---------- 共享 API ----------

export const shareApi = {
  /** 创建分享 */
  create: (data: { entity_type: string; entity_id: number; username: string; permission: string }) =>
    api.post<import('../types').ShareInfo>('/api/share/create', data),

  /** 撤销分享 */
  revoke: (shareId: number) =>
    api.delete<{ success: boolean; message: string }>(`/api/share/${shareId}`),

  /** 我分享出去的 */
  sharedByMe: (entityType?: string, entityId?: number) =>
    api.get<import('../types').ShareListResponse>('/api/share/shared-by-me', {
      params: entityType && entityId ? { entity_type: entityType, entity_id: entityId } : undefined,
    }),

  /** 分享给我的 */
  sharedWithMe: () =>
    api.get<import('../types').ShareListResponse>('/api/share/shared-with-me'),

  /** 修改共享权限 */
  updatePermission: (shareId: number, permission: string) =>
    api.put<import('../types').ShareInfo>(`/api/share/${shareId}`, { permission }),

  /** 检查用户名是否存在 */
  checkUser: (username: string) =>
    api.post<import('../types').CheckUserResponse>('/api/share/check-user', { username }),

  /** 转移所有权 */
  transferOwnership: (data: import('../types').TransferOwnershipRequest) =>
    api.post<import('../types').TransferOwnershipResponse>('/api/share/transfer-ownership', data),

  /** 模糊搜索可分享用户（分享下拉） */
  searchUsers: (keyword: string, limit: number = 20) =>
    api.get<import('../types').UserSearchResponse>('/api/share/search-users', { params: { keyword, limit } }),
};

// ===================== 招标信息筛选 =====================

export const screeningApi = {
  /** 招标信息列表 */
  list: (params?: { decision?: string; prescreen?: string; keyword?: string }) =>
    api.get<{
      success: boolean;
      items: import('../types/screening').ScreeningOpportunity[];
      total: number;
    }>('/api/screening/opportunities', { params }),

  /** 创建招标信息 */
  create: (data: {
    name?: string;
    source_type?: 'text' | 'file' | 'url';
    source_url?: string | null;
    raw_text?: string | null;
    source_file_name?: string | null;
    company_profile_id?: number | null;
  }) =>
    api.post<{ success: boolean; data: import('../types/screening').ScreeningOpportunity }>(
      '/api/screening/opportunities',
      data,
    ),

  /** 招标信息详情（含逐条要求与历次结论） */
  get: (id: number) =>
    api.get<{ success: boolean; data: import('../types/screening').ScreeningOpportunity }>(
      `/api/screening/opportunities/${id}`,
    ),

  /** 更新招标信息（人工修正或决策） */
  update: (id: number, data: Record<string, unknown>) =>
    api.put<{ success: boolean; data: import('../types/screening').ScreeningOpportunity }>(
      `/api/screening/opportunities/${id}`,
      data,
    ),

  /** 删除招标信息 */
  remove: (id: number) =>
    api.delete<{ success: boolean; message: string }>(`/api/screening/opportunities/${id}`),

  /** 抓取公告网址并预览正文（不落库） */
  fetchPreview: (url: string) =>
    api.post<{
      success: boolean;
      data: import('../types/screening').FetchPreviewResult;
      message: string;
    }>('/api/screening/fetch-preview', { url }),

  /** 上传公告文件并提取文本（不落库） */
  extractFile: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<{ success: boolean; data: import('../types/screening').ExtractFileResult }>(
      '/api/screening/extract-file',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 },
    );
  },

  /** 人工修正单条要求的判定 */
  updateRequirement: (
    requirementId: number,
    data: { final_verdict?: string | null; final_note?: string | null; is_mandatory?: boolean },
  ) =>
    api.put<{
      success: boolean;
      data: import('../types/screening').ScreeningRequirement;
      message: string;
    }>(`/api/screening/requirements/${requirementId}`, data),

  /**
   * 人工确认某家企业某条要求的判定（企业级：只对该企业生效，重跑审核时保留并优先）
   * final_verdict 传空字符串表示取消人工确认、恢复模型判定
   */
  updateCompanyScore: (
    scoreId: number,
    data: { final_verdict?: string; final_note?: string | null },
  ) =>
    api.put<{
      success: boolean;
      data: import('../types/screening').ScreeningCompanyScoreItem;
      message: string;
    }>(`/api/screening/company-scores/${scoreId}`, data),

  /** 转入投标文件编写 */
  convertToProject: (id: number) =>
    api.post<{ success: boolean; project_id: number; created: boolean; message: string }>(
      `/api/screening/opportunities/${id}/convert-to-project`,
      {},
    ),

  /** 批量导入公告网址（每行一个） */
  batchImport: (data: { urls: string[]; company_profile_id?: number | null }) =>
    api.post<import('../types/screening').BatchImportResult>('/api/screening/batch-import', data),

  /** 记录招标实际结果（中标/未中标/放弃） */
  updateOutcome: (id: number, data: { outcome: string; outcome_note?: string | null }) =>
    api.put<{
      success: boolean;
      data: import('../types/screening').ScreeningOpportunity;
      message: string;
    }>(`/api/screening/opportunities/${id}/outcome`, data),

  /** 决策复盘统计 */
  stats: () =>
    api.get<{ success: boolean; data: import('../types/screening').ScreeningStats }>(
      '/api/screening/stats',
    ),

  /** SSE：批量初筛（抓取正文 + 匹配度粗筛） */
  batchPrescreenStream: async (
    opportunityIds: number[],
    onEvent: (event: import('../types/screening').ScreeningStreamEvent) => void,
    signal?: AbortSignal,
  ): Promise<void> => {
    const token = localStorage.getItem('auth_token');
    const res = await fetch(`${API_BASE_URL}/api/screening/batch-prescreen-stream`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ opportunity_ids: opportunityIds }),
      signal,
    });
    await readSseStream(
      res,
      (event) => onEvent(event as unknown as import('../types/screening').ScreeningStreamEvent),
      '批量初筛流中断',
      signal,
    );
  },

  /** SSE：抓取（如需）并抽取公告要点 */
  ingestStream: async (
    opportunityId: number,
    onEvent: (event: import('../types/screening').ScreeningStreamEvent) => void,
    signal?: AbortSignal,
  ): Promise<void> => {
    const token = localStorage.getItem('auth_token');
    const res = await fetch(
      `${API_BASE_URL}/api/screening/opportunities/${opportunityId}/ingest-stream`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        signal,
      },
    );
    await readSseStream(
      res,
      (event) => onEvent(event as unknown as import('../types/screening').ScreeningStreamEvent),
      '要点抽取流中断',
      signal,
    );
  },

  /**
   * SSE：单企业资质比对与投标决策（已下线，端点保留以兼容历史数据）
   * 新的审核入口统一走 compareCompaniesStream（只选 1 家即为单企业结论）
   */
  evaluateStream: async (
    opportunityId: number,
    companyProfileId: number | null | undefined,
    onEvent: (event: import('../types/screening').ScreeningStreamEvent) => void,
    signal?: AbortSignal,
  ): Promise<void> => {
    const token = localStorage.getItem('auth_token');
    const query = companyProfileId ? `?company_profile_id=${companyProfileId}` : '';
    const res = await fetch(
      `${API_BASE_URL}/api/screening/opportunities/${opportunityId}/evaluate-stream${query}`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        signal,
      },
    );
    await readSseStream(
      res,
      (event) => onEvent(event as unknown as import('../types/screening').ScreeningStreamEvent),
      '投标决策评估流中断',
      signal,
    );
  },

  /** SSE：多企业是否适合投标审核（逐家核算客观分失分与门槛，推荐适合投标的企业） */
  compareCompaniesStream: async (
    opportunityId: number,
    companyProfileIds: number[],
    onEvent: (event: import('../types/screening').ScreeningStreamEvent) => void,
    signal?: AbortSignal,
  ): Promise<void> => {
    const token = localStorage.getItem('auth_token');
    const res = await fetch(
      `${API_BASE_URL}/api/screening/opportunities/${opportunityId}/company-compare-stream`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ company_profile_ids: companyProfileIds }),
        signal,
      },
    );
    await readSseStream(
      res,
      (event) => onEvent(event as unknown as import('../types/screening').ScreeningStreamEvent),
      '是否适合投标审核流中断',
      signal,
    );
  },

  /** 读取最近一次多企业是否适合投标审核结果（含逐条明细矩阵） */
  getCompanyComparison: (opportunityId: number) =>
    api.get<{
      success: boolean;
      data: import('../types/screening').ScreeningCompanyComparison | null;
    }>(`/api/screening/opportunities/${opportunityId}/company-comparisons`),
};

/** 招标文件编写模块（招标方视角） */
export const tenderApi = {
  /** 模板列表（系统内置 + 我的模板） */
  listTemplates: (params?: { purchase_method?: string; industry?: string }) =>
    api.get<{ success: boolean; items: import('../types/tender').TenderTemplate[] }>(
      '/api/tender/templates',
      { params },
    ),

  /** 模板详情 */
  getTemplate: (templateId: number) =>
    api.get<{ success: boolean; data: import('../types/tender').TenderTemplate }>(
      `/api/tender/templates/${templateId}`,
    ),

  /** 新建自有模板 */
  createTemplate: (data: {
    name: string;
    purchase_method?: string;
    industry?: string;
    description?: string | null;
    structure: import('../types/tender').TenderStructureNode[];
  }) =>
    api.post<{ success: boolean; data: import('../types/tender').TenderTemplate }>(
      '/api/tender/templates',
      data,
    ),

  /** 我的招标文件项目列表 */
  listProjects: () =>
    api.get<{ success: boolean; items: import('../types/tender').TenderProject[]; total: number }>(
      '/api/tender/projects',
    ),

  /** 招标文件项目回收站（软删除的项目） */
  listTrash: () =>
    api.get<import('../types').TrashListResponse>('/api/project/trash', {
      params: { doc_kind: 'tender' },
    }),

  /** 从回收站恢复项目 */
  restore: (projectId: number) => api.post(`/api/project/${projectId}/restore`),

  /** 按模板创建招标文件项目（含立项信息与目录实例化） */
  createProject: (data: {
    name: string;
    template_id: number;
    description?: string | null;
    info: import('../types/tender').TenderInfoPayload;
  }) =>
    api.post<{
      success: boolean;
      data: {
        project: import('../types/tender').TenderProject;
        info: import('../types/tender').TenderProjectInfo;
      };
      message: string;
    }>('/api/tender/projects', data),

  /** 项目详情：项目 + 立项信息 + 目录 + 已生成正文 */
  getProject: (projectId: number) =>
    api.get<{ success: boolean; data: import('../types/tender').TenderProjectDetail }>(
      `/api/tender/projects/${projectId}`,
    ),

  /** 保存招标文件目录（整棵目录全量替换，支持增删改与层级调整，正文按 stable_id 保留） */
  updateOutline: (
    projectId: number,
    nodes: Array<{
      title: string;
      description?: string | null;
      word_count_range?: string | null;
      part_kind?: string | null;
      section?: string | null;
      stable_id?: string | null;
      children?: unknown[];
    }>,
  ) =>
    api.put<{
      success: boolean;
      total: number;
      orphan_content_count: number;
      message: string;
    }>(`/api/tender/projects/${projectId}/outline`, { nodes }),

  /** 按当前模板同步目录章节标题与分部（保留已生成正文与人工修改） */
  syncOutline: (projectId: number) =>
    api.post<{ success: boolean; updated: number; message: string }>(
      `/api/tender/projects/${projectId}/sync-outline`,
    ),

  /** 按模板重新生成目录：merge=补齐缺失章节（保留现有章节/人工增补/正文）；replace=按模板重建（正文按标题匹配保留） */
  regenerateOutline: (
    projectId: number,
    data: { template_id?: number | null; mode?: 'merge' | 'replace' },
  ) =>
    api.post<{
      success: boolean;
      mode: string;
      template_id: number;
      template_name: string;
      total: number;
      inherited: number;
      message: string;
    }>(`/api/tender/projects/${projectId}/regenerate-outline`, data),

  /** 更新立项信息（只传需要改的字段） */
  updateInfo: (projectId: number, data: import('../types/tender').TenderInfoPayload) =>
    api.put<{
      success: boolean;
      data: import('../types/tender').TenderProjectInfo;
      message: string;
    }>(`/api/tender/projects/${projectId}/info`, data),

  /** 上传历史招标文件 → 解析章节结构（不落库，前端确认后再保存为模板） */
  parseTemplateFile: (file: File, name?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<{ success: boolean; data: import('../types/tender').TenderTemplateParseResult }>(
      `/api/tender/templates/parse${name ? `?name=${encodeURIComponent(name)}` : ''}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 },
    );
  },

  /** 条款库列表 */
  listClauses: (params?: { category?: string; purchase_method?: string; industry?: string; keyword?: string }) =>
    api.get<{ success: boolean; items: import('../types/tender').TenderClause[]; total: number }>(
      '/api/tender/clauses',
      { params },
    ),

  /** 新增条款 */
  createClause: (data: {
    category?: string;
    title: string;
    content: string;
    purchase_methods?: string[];
    industry?: string | null;
    source?: string | null;
    legal_basis?: string | null;
    tags?: string[];
    is_active?: boolean;
  }) =>
    api.post<{ success: boolean; data: import('../types/tender').TenderClause }>(
      '/api/tender/clauses',
      data,
    ),

  /** 更新条款 */
  updateClause: (clauseId: number, data: Record<string, unknown>) =>
    api.put<{ success: boolean; data: import('../types/tender').TenderClause }>(
      `/api/tender/clauses/${clauseId}`,
      data,
    ),

  /** 删除条款 */
  deleteClause: (clauseId: number) =>
    api.delete<{ success: boolean; message: string }>(`/api/tender/clauses/${clauseId}`),

  /** 价格参考列表 */
  listPriceReferences: (params?: { category?: string; region?: string; keyword?: string }) =>
    api.get<{
      success: boolean;
      items: import('../types/tender').TenderPriceReference[];
      total: number;
    }>('/api/tender/price-references', { params }),

  /** 新增价格参考 */
  createPriceReference: (data: {
    name: string;
    price: number;
    price_type?: string;
    category?: string | null;
    region?: string | null;
    unit?: string | null;
    source?: string | null;
    reference_date?: string | null;
    note?: string | null;
  }) =>
    api.post<{ success: boolean; data: import('../types/tender').TenderPriceReference }>(
      '/api/tender/price-references',
      data,
    ),

  /** 更新价格参考 */
  updatePriceReference: (referenceId: number, data: Record<string, unknown>) =>
    api.put<{ success: boolean; data: import('../types/tender').TenderPriceReference }>(
      `/api/tender/price-references/${referenceId}`,
      data,
    ),

  /** 删除价格参考 */
  deletePriceReference: (referenceId: number) =>
    api.delete<{ success: boolean; message: string }>(
      `/api/tender/price-references/${referenceId}`,
    ),

  /** 合规检查项列表（系统内置 + 自定义） */
  listComplianceRules: () =>
    api.get<{
      success: boolean;
      items: import('../types/tender').TenderComplianceRule[];
      total: number;
    }>('/api/tender/compliance-rules'),

  /** 新增自定义合规检查项 */
  createComplianceRule: (data: {
    title: string;
    check_prompt: string;
    category?: string;
    code?: string;
    legal_basis?: string | null;
    severity?: string;
    purchase_methods?: string[];
    industry?: string | null;
  }) =>
    api.post<{ success: boolean; data: import('../types/tender').TenderComplianceRule }>(
      '/api/tender/compliance-rules',
      data,
    ),

  /** 更新自定义合规检查项（内置检查项不可改） */
  updateComplianceRule: (
    ruleId: number,
    data: {
      title?: string;
      check_prompt?: string;
      severity?: string;
      category?: string;
      legal_basis?: string | null;
      purchase_methods?: string[];
      industry?: string | null;
      is_active?: boolean;
    },
  ) =>
    api.put<{ success: boolean; data: import('../types/tender').TenderComplianceRule }>(
      `/api/tender/compliance-rules/${ruleId}`,
      data,
    ),

  /** 删除自定义合规检查项 */
  deleteComplianceRule: (ruleId: number) =>
    api.delete<{ success: boolean; message: string }>(
      `/api/tender/compliance-rules/${ruleId}`,
    ),

  /** 读取最近一次合规审核报告 */
  getComplianceReport: (projectId: number) =>
    api.get<{
      success: boolean;
      data: import('../types/tender').TenderComplianceReport | null;
    }>(`/api/tender/projects/${projectId}/compliance-report`),

  /** SSE：执行合规审核（规则 + 语义） */
  complianceCheckStream: async (
    projectId: number,
    onEvent: (event: {
      type?: 'progress' | 'result';
      message?: string;
      report?: import('../types/tender').TenderComplianceReport;
      error?: boolean;
    }) => void,
    signal?: AbortSignal,
  ): Promise<void> => {
    const token = localStorage.getItem('auth_token');
    const res = await fetch(
      `${API_BASE_URL}/api/tender/projects/${projectId}/compliance-check-stream`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        signal,
      },
    );
    await readSseStream(res, (event) => onEvent(event), '合规审核流中断', signal);
  },

  /** SSE：流式编制单个章节（Markdown 增量输出） */
  generatePartStream: async (
    projectId: number,
    stableId: string,
    onEvent: (event: {
      type?: 'progress' | 'result';
      message?: string;
      chunk?: string;
      content?: string;
      error?: boolean;
    }) => void,
    signal?: AbortSignal,
  ): Promise<void> => {
    const token = localStorage.getItem('auth_token');
    const res = await fetch(
      `${API_BASE_URL}/api/tender/projects/${projectId}/parts/${stableId}/generate-stream`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        signal,
      },
    );
    await readSseStream(res, (event) => onEvent(event), '章节编制流中断', signal);
  },

  /** SSE：批量编制章节（按目录顺序逐章生成，每章完成后立即落库） */
  generateAllStream: async (
    projectId: number,
    data: { skip_existing?: boolean; stable_ids?: string[] },
    onEvent: (event: {
      type?: 'progress' | 'result';
      message?: string;
      stable_id?: string;
      node_id?: string;
      title?: string;
      content?: string;
      index?: number;
      total?: number;
      saved?: boolean;
      finished?: boolean;
      generated?: number;
      error?: boolean;
    }) => void,
    signal?: AbortSignal,
  ): Promise<void> => {
    const token = localStorage.getItem('auth_token');
    const res = await fetch(
      `${API_BASE_URL}/api/tender/projects/${projectId}/generate-all-stream`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
        signal,
      },
    );
    await readSseStream(res, (event) => onEvent(event), '批量编制流中断', signal);
  },
};

export default api;
